import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, User, Mail, Shield, Key, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const MyProfile = ({ user }) => {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    full_name: user.full_name,
    email: user.email,
    phone: user.phone || ''
  });

  const handleUpdateProfile = async () => {
    try {
      await axiosInstance.put(`${API}/users/me`, editData);
      toast.success('Profile updated successfully. Please log in again.');
      // Logout and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button onClick={() => navigate('/')} variant="outline" size="icon" data-testid="back-from-profile-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold text-slate-800">My Profile</h2>
      </div>

      <div className="max-w-2xl">
        {/* Personal Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
            {user.role === 'admin' && (
              <Button
                onClick={() => setEditMode(!editMode)}
                variant="outline"
                size="sm"
                data-testid="profile-edit-btn"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                {editMode ? 'Cancel' : 'Edit'}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editMode ? (
              <>
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={editData.full_name}
                    onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                    data-testid="edit-profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    data-testid="edit-profile-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    data-testid="edit-profile-phone-input"
                  />
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Role</p>
                  <p className="font-semibold text-slate-800 capitalize flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {user.role}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Role cannot be changed</p>
                </div>
                <Button 
                  onClick={handleUpdateProfile} 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  data-testid="save-profile-btn"
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Full Name</p>
                  <p className="font-semibold text-slate-800" data-testid="profile-name">{user.full_name}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Email</p>
                  <p className="font-semibold text-slate-800 flex items-center gap-2" data-testid="profile-email">
                    <Mail className="h-4 w-4" />
                    {user.email}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Role</p>
                  <p className="font-semibold text-slate-800 capitalize flex items-center gap-2" data-testid="profile-role">
                    <Shield className="h-4 w-4" />
                    {user.role}
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MyProfile;
